/**
 * When the Stream Manager's Stream Recordings list refetches.
 *
 * EN-1261: the backend broadcasts ReceiveStreamRecordingUpdate to the
 * device's group when anyone starts a recording or stops one manually.
 * EN-1554: it also sends one (isActive: false) when anyone in the group
 * deletes a recording, so the deleted row leaves the other lists.
 * The web dispatches streamRecordingUpdateReceived and the open list
 * refetches quietly (no popup, no notice). The open stream's plot
 * follows the same message through StreamRecordingStopSync: a stop of its
 * recording resets it (EN-1552), and a start on its device turns it to
 * Stop Rec (EN-1556).
 */

/** SignalR event sent by the AgmHub for a manual start or stop, or a delete. */
export const STREAM_RECORDING_UPDATE_EVENT = 'ReceiveStreamRecordingUpdate';

/** Signal-only Vuex action dispatched for that event. */
export const STREAM_RECORDING_UPDATE_ACTION = 'streamRecordingUpdateReceived';

/** Payload of ReceiveStreamRecordingUpdate (StreamRecordingUpdateMsg). */
export interface StreamRecordingUpdateMsg {
  uid: number;
  streamRecordingId: number;
  /** true = started, false = stopped or deleted (EN-1554). */
  isActive: boolean;
  timestampUtc?: string;
}

/**
 * Actions from this browser (local start/stop) or the auto-stop
 * broadcast. These always refetch.
 */
const LOCAL_RECORDING_CHANGE_ACTIONS: readonly string[] = [
  'recordingStarted',
  'recordingStopped',
  'streamRecordingAutoStopped',
];

/**
 * True when this action means the recordings list may be out of date.
 * A group update from another user only refetches while the Stream
 * Manager is visible; opening the panel fetches anyway.
 */
export function shouldRefreshRecordings(
  actionType: string,
  visible = true
): boolean {
  if (actionType === STREAM_RECORDING_UPDATE_ACTION) {
    return visible;
  }
  return LOCAL_RECORDING_CHANGE_ACTIONS.includes(actionType);
}

/**
 * True when the refresh was triggered by a group broadcast from another
 * user, i.e. in the background, not by the user opening or refreshing the
 * list or by a start/stop in this browser.
 */
export function isBackgroundRecordingRefresh(actionType?: string): boolean {
  return actionType === STREAM_RECORDING_UPDATE_ACTION;
}

/**
 * The list to show after a failed fetch. A background refresh keeps the
 * list the user is looking at; a manual open or refresh clears it, as
 * before.
 */
export function recordingsAfterFailedFetch<T>(
  previous: T[],
  background: boolean
): T[] {
  return background ? previous : [];
}

interface ActionSubscriber {
  subscribeAction(
    fn: (action: { type: string; payload?: any }) => void
  ): () => void;
}

/**
 * Calls refresh(actionType) whenever a recording-change action is
 * dispatched. Returns the unsubscribe function.
 */
export function subscribeRecordingRefresh(
  store: ActionSubscriber,
  refresh: (actionType: string) => void,
  isVisible: () => boolean = () => true
): () => void {
  return store.subscribeAction((action) => {
    if (shouldRefreshRecordings(action.type, isVisible())) {
      refresh(action.type);
    }
  });
}

interface HubConnectionLike {
  on(eventName: string, handler: (data: any) => void): void;
}

/**
 * Registers the ReceiveStreamRecordingUpdate handler on the AgmHub
 * connection. Only devices in the user's group are passed on, as
 * streamRecordingUpdateReceived.
 */
export function registerStreamRecordingUpdateHandler(
  connection: HubConnectionLike,
  isUidInGroup: (uid: number) => boolean,
  dispatch: (type: string, payload: StreamRecordingUpdateMsg) => void
): void {
  connection.on(STREAM_RECORDING_UPDATE_EVENT, (data: any) => {
    if (data && isUidInGroup(data.uid)) {
      dispatch(STREAM_RECORDING_UPDATE_ACTION, data);
    }
  });
}
